/**
 * Compile-time-composed inbound dispatch core for the embedded bridge.
 *
 * Feature modules each export a `HandlerMap` (verb → handler). They are merged
 * once at module load via `registerFeatureMaps` into a single lookup TABLE, so
 * `window.__previsRecv` resolves an inbound message to exactly one owner with a
 * plain object lookup — no per-verb receiver chaining, no multicast.
 *
 * The legacy inbound switch in embeddedBridge.ts was deleted in Bridge 12/14
 * (Task 13); every verb now resolves through the merged TABLE. `LEGACY_SWITCH_TYPES`
 * is permanently empty but retained: assertBridgeTableComplete() unions it with the
 * TABLE keys as the runtime completeness belt to the compile-time BridgeHandlers
 * exhaustiveness suspenders.
 */

import { enqueueSendToNative } from './transport';
import { flushSceneReadyBuffer } from '@/store/sceneReadyBuffer';

export const ALL_BRIDGE_TYPES = [
  'setSqlitePath', 'updateShot', 'setActiveScene', 'selectShot', 'seek', 'sceneReady',
  'thumbnailChanged', 'boardFillState', 'allScenesReady', 'canvasEntryFinished', 'setShotFullImage',
  // 20260922 — the Cast list's per-character portraits, pushed after every project push (and after
  // a generation pass wrote new files). Full-set REPLACE, see features/castHeadshots.ts.
  'setCharacterHeadshots',
  // 20260925 — the native image pick's answer (Add shot chooser → Import an image). See
  // features/imagePick.ts for the contract.
  'shotImagePicked',
  'setAuth', 'authFailed', 'signinSucceeded', 'useStandaloneAuth', 'clearAuth',
  'setSubscriptionState', 'setStorefrontRegion', 'creditPurchaseResult',
  'shotCreationResult', 'startReimport', 'reimportComplete', 'reimportError',
  'setCatalog', 'castCatalog', 'coverageApplied', 'roughBlockApplied', 'requestCastPipeline', 'setRecipe', 'setCharacterNames', 'setRequireName', 'setLegacyAvailability', 'assetReady', 'assetFailed', 'setTripoCharacterCost', 'setCreatorTour',
  'recordingStarted', 'recordingComplete', 'recordingFailed', 'audioClipReady', 'audioClipFailed', 'setAudioClipBytes',
  'teamAccessState', 'teamShareError', 'teamUploadProgress', 'socialShareResult', 'exportShareResult',
  'exportNLEProgress', 'exportNLEComplete', 'exportHtmlComplete',
  'setUIScaling', 'setLocale', 'setFeatureFlags', 'setInputCapabilities', 'setFoldState', 'setDeviceId', 'setFunnelId',
  'closePanel', 'assetManifest', 'setSceneSettings', 'setFloorPreview', 'setSkyPreview', 'setShotSettings', 'setProjectSettings', 'setWatermarkLogoPreviews', 'setProjectSettingsPreview',
  'shotDeleted', 'shotsDeleted', 'sceneDeleted', 'shotMutated', 'sceneMutated', 'copyBoardState', 'setShotGradePreview',
  'setSetLibrary', 'applySetApplied', 'sceneSavedAsSet', 'customSetDeleted', 'setSetPreviews',
  // 20260921 (design §2.5) — the Characters tour's two extra inbound verbs. `exitTutorial` is
  // Unity ending a tour from its side (the Character Creator's own Skip tour); `castMemberAdded`
  // is NOT tour-gated and lands every creator save on the new cast card.
  'startTutorial', 'exitTutorial', 'castMemberAdded',
  // 20260928 — the canvas overlay (?page=canvas-overlay, Unity's CanvasWebOverlayPopupPanel): the
  // view to show over the live canvas, the menu's context rows, the fold-split touch region, and
  // the What's New dot (also pushed to the board). See features/canvasOverlay.ts.
  'showView', 'setOverlayContext', 'setOverlayRegion', 'setWhatsNewBadge',
  // 20261002 — the Shot Editor coach marks' native control rectangles (same module).
  'setOrientTargets',
  // 20261006 — the opener's native top bar bottom, for the macOS docked sheet (same module).
  'setOverlayTopBar',
  // 20261005 — the canvas Coverage overview (?page=canvas-coverage): the mm one shot's canvas tile
  // now shows. See features/canvasCoverage.ts.
  'coverageShotLens',
] as const;
export type BridgeMessageType = (typeof ALL_BRIDGE_TYPES)[number];
export type HandlerMap = Partial<Record<BridgeMessageType, (msg: never) => void>>;

/** Verbs owned by the legacy inbound switch that once lived in embeddedBridge.ts.
 * That switch was deleted in Bridge 12/14 (Task 13); every verb now resolves through
 * the merged TABLE, so this set is permanently empty. Retained (not removed) because
 * assertBridgeTableComplete() unions it with the TABLE keys — a future verb added to
 * ALL_BRIDGE_TYPES without a registered handler still trips the runtime assertion. */
export const LEGACY_SWITCH_TYPES = new Set<string>();

const TABLE: Record<string, (msg: never) => void> = {};
export function registerFeatureMaps(maps: HandlerMap[]): void {
  for (const m of maps) {
    for (const k of Object.keys(m)) {
      if (TABLE[k]) {
        const err = `[bridge] duplicate handler for '${k}' — one verb, one owner`;
        if (import.meta.env.PROD) { console.error(err); continue; }
        throw new Error(err);
      }
      TABLE[k] = (m as Record<string, (msg: never) => void>)[k];
    }
  }
}

/** Dev-only seam for embeddedBridge's HMR dispose hook: empties the table so the re-evaluated
 *  bridge module can register its maps again without tripping the duplicate-verb guard above.
 *  This module is NOT re-evaluated by that update (it is a dependency, not an importer), so the
 *  table would otherwise survive with every verb already owned. Never called in production. */
export function resetFeatureMaps(): void {
  for (const k of Object.keys(TABLE)) delete TABLE[k];
}

interface Unclaimed { count: number; ring: Array<{ type: string; t: number }> }
const unclaimed: Unclaimed = { count: 0, ring: [] };
(window as unknown as Record<string, unknown>).__bridgeUnclaimed = unclaimed;
function recordUnclaimed(type: string): void {
  unclaimed.count += 1;
  unclaimed.ring.push({ type, t: Date.now() });
  if (unclaimed.ring.length > 50) unclaimed.ring.shift();
  console.warn(`[bridge] unclaimed inbound message type '${type}'`);
  // Unconditional (not flag-gated) report to Unity's console via the native diagLog
  // verb — unlike transport's diagLog() helper, this must always fire so unclaimed
  // verbs are visible in the field, not just when diagnostic logging is enabled.
  enqueueSendToNative('diagLog', { msg: `[bridge] unclaimed inbound message type '${type}' (count=${unclaimed.count})` });
}

export function dispatchMessage(raw: unknown): void {
  const r = raw as { type?: unknown; action?: unknown; payload?: unknown } | null;
  const type = typeof r?.type === 'string' ? r.type : typeof r?.action === 'string' ? r.action : null;
  if (type === null) { recordUnclaimed(String((r as { type?: unknown })?.type)); return; }
  const handler = TABLE[type];
  if (handler) {
    // Ordering seam for the sceneReady buffer (sceneReadyBuffer.ts). sceneReady is the one verb
    // whose store write is deferred; every OTHER verb drains it first, so a buffered tile push
    // can never land on top of a mutation that arrived after it. One seam here rather than a
    // flush call per mutating handler — the per-handler version is a list a future verb forgets
    // to join, and this is a no-op on an empty buffer.
    if (type !== 'sceneReady') flushSceneReadyBuffer();
    const msg = { ...(raw as object), type, payload: r?.payload ?? raw };
    try { handler(msg as never); } catch (e) { console.error(`[bridge] handler for '${type}' threw`, e); }
    return;
  }
  recordUnclaimed(type);
}

export function assertBridgeTableComplete(): void {
  const covered = new Set<string>([...Object.keys(TABLE), ...LEGACY_SWITCH_TYPES]);
  const missing = ALL_BRIDGE_TYPES.filter((t) => !covered.has(t));
  const unknown = Object.keys(TABLE).filter((t) => !(ALL_BRIDGE_TYPES as readonly string[]).includes(t));
  if (missing.length === 0 && unknown.length === 0) return;
  const err = `[bridge] handler table incomplete — missing: [${missing.join(', ')}] unknown: [${unknown.join(', ')}]`;
  if (import.meta.env.PROD) { console.error(err); return; }
  throw new Error(err);
}

type Receiver = (msg: unknown) => void;
/** Whole-receiver decorator: capture current __previsRecv, install a wrapper that can
 * fall through to it, return a restore fn. NEVER per-verb, never multicast.
 *
 * CONSTRAINT — a wrapper must NEVER claim a project-mutating verb. dispatchMessage drains the
 * sceneReady buffer (store/sceneReadyBuffer.ts) before every other verb's handler, which is what
 * guarantees a buffered tile push can't land on top of a later mutation. A wrapper that handles a
 * verb and returns WITHOUT calling next(msg) never reaches dispatchMessage, so it silently opts
 * that verb out of the seam. The seam's own comment above argues that a per-handler flush is "a
 * list a future verb forgets to join"; this is the one remaining way to leave that list, and the
 * resulting corruption is silent, data-shaped, and invisible to every test. Claim only verbs that
 * touch local/component state (today: preferences, manage-subscription, project-list and
 * share/backup verbs — none of which write useProjectStore), or call next(msg) as well. */
export function wrapReceiver(make: (next: Receiver) => Receiver): () => void {
  const w = window as unknown as Record<string, unknown>;
  const prev = (w.__previsRecv as Receiver | undefined) ?? (() => {});
  const wrapped = make(prev);
  w.__previsRecv = wrapped;
  return () => { if (w.__previsRecv === wrapped) w.__previsRecv = prev; };
}
